// ---------------------------------------------------------------------------
// Securities engagement letter model
// ---------------------------------------------------------------------------
// A securities attorney's rate card carries a structured "engagement letter"
// describing how their fees are billed: per-deliverable fee lines (flat or
// hourly), an optional refundable cost retainer, and hourly rates by role
// for additional / out-of-scope work. Each fee line says when it bills via
// a billing trigger that can be a single event, a fixed date, or a split
// installment schedule whose installments may individually trigger on the
// earlier of an event/date.
//
// This file is the canonical type for the `attorneys.securities_engagement`
// jsonb column AND for the `project_billing_installments.source_spec`
// snapshot the per-project schedule is generated from.

import { z } from "zod";

export const HOURLY_ROLES = ["partner", "senior_associate", "associate", "paralegal"] as const;
export type HourlyRole = (typeof HOURLY_ROLES)[number];

export const HOURLY_ROLE_LABEL: Record<HourlyRole, string> = {
  partner: "Partner",
  senior_associate: "Senior Associate",
  associate: "Associate",
  paralegal: "Paralegal",
};

// Trigger events keyed off project milestones / state. Generic across firms.
export const BILLING_TRIGGER_EVENTS = [
  "engagement",
  "before_commencement",        // before offering commences (i.e. PPM ready)
  "working_draft_delivered",    // first working draft of offering documents
  "offering_documents_delivered",
  "offering_commenced",
  "loan_documents_delivered",
] as const;
export type BillingTriggerEvent = (typeof BILLING_TRIGGER_EVENTS)[number];

export const BILLING_TRIGGER_EVENT_LABEL: Record<BillingTriggerEvent, string> = {
  engagement: "On engagement",
  before_commencement: "Before offering commences",
  working_draft_delivered: "Working draft of offering docs delivered",
  offering_documents_delivered: "Offering documents finalized",
  offering_commenced: "Offering commenced",
  loan_documents_delivered: "Loan documents delivered",
};

// A single trigger; either an event, a fixed date, or "earlier of" both.
export const billingTriggerSchema = z.object({
  type: z.enum(["event", "date", "earlier_of"]),
  event: z.enum(BILLING_TRIGGER_EVENTS).nullable().optional(),
  // YYYY-MM-DD; for "date" type it's required, for "earlier_of" it's the
  // date side of the earlier-of pair.
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
}).refine(t => {
  if (t.type === "event") return !!t.event;
  if (t.type === "date") return !!t.date;
  if (t.type === "earlier_of") return !!t.event && !!t.date;
  return false;
}, { message: "Trigger is missing required event or date" });
export type BillingTrigger = z.infer<typeof billingTriggerSchema>;

// Single non-installment trigger (the whole flat fee bills at once).
export const singleBillingSchema = z.object({
  kind: z.literal("single"),
  trigger: billingTriggerSchema,
});

// Split: each installment has its own dollar amount and trigger.
export const splitInstallmentSchema = z.object({
  amount: z.coerce.number().int().min(0),
  label: z.string().max(120).optional(),
  trigger: billingTriggerSchema,
});

export const splitBillingSchema = z.object({
  kind: z.literal("split"),
  installments: z.array(splitInstallmentSchema).min(1),
});

export const billingSpecSchema = z.discriminatedUnion("kind", [singleBillingSchema, splitBillingSchema]);
export type BillingSpec = z.infer<typeof billingSpecSchema>;

export const feeLineSchema = z.object({
  key: z.string().min(1).max(80),
  label: z.string().min(1).max(160),
  type: z.enum(["flat", "hourly"]),
  // Flat fee amount. Ignored for hourly lines. For split-billing the sum of
  // installments is the source of truth; this field is informational.
  amount: z.coerce.number().int().min(0).nullable().optional(),
  // Securities checklist item keys this fee bundles (e.g. ppm,
  // operating_agreement, etc.). Empty for hourly-only lines.
  deliverableKeys: z.array(z.string()).default([]),
  // True when revisions to the bundled deliverables are billed hourly
  // rather than included in the flat fee (Loan Documents Fee).
  revisionsHourly: z.boolean().default(false),
  // Conditional lines (SEC Form D, Intercreditor Agreement) only apply
  // when the project admin marks them applicable.
  conditional: z.boolean().default(false),
  // Billing spec — flat lines must have one; hourly lines may omit.
  billing: billingSpecSchema.nullable().optional(),
});
export type FeeLine = z.infer<typeof feeLineSchema>;

export const securitiesEngagementSchema = z.object({
  presetKey: z.string().nullable().optional(), // e.g. "torres" — informational
  costRetainerAmount: z.coerce.number().int().min(0).nullable().optional(),
  hourlyRates: z.partialRecord(z.enum(HOURLY_ROLES), z.coerce.number().int().min(0).nullable()).default({}),
  feeLines: z.array(feeLineSchema).default([]),
});
export type SecuritiesEngagement = z.infer<typeof securitiesEngagementSchema>;

// Empty default used by client form / fallback when column is null.
export const EMPTY_SECURITIES_ENGAGEMENT: SecuritiesEngagement = {
  presetKey: null,
  costRetainerAmount: null,
  hourlyRates: {},
  feeLines: [],
};

// ---------------------------------------------------------------------------
// Torres preset — matches Engagement_Letter_EB5.Unaffiliated_Project_Form.
// ---------------------------------------------------------------------------
export const TORRES_PRESET_KEY = "torres";

export const TORRES_ENGAGEMENT_PRESET: SecuritiesEngagement = {
  presetKey: TORRES_PRESET_KEY,
  costRetainerAmount: 2000,
  hourlyRates: {
    partner: 750,
    senior_associate: 575,
    associate: 450,
    paralegal: 225,
  },
  feeLines: [
    {
      key: "offering_documents",
      label: "Offering Documents Fee",
      type: "flat",
      amount: 50000,
      deliverableKeys: [
        "ppm",
        "operating_agreement",
        "subscription_agreement",
        "escrow_agreement",
        "business_plan_review",
        "economic_report_review",
        "compliance_certificates",
        "due_diligence",
      ],
      revisionsHourly: false,
      conditional: false,
      billing: {
        kind: "split",
        installments: [
          {
            amount: 30000,
            label: "On engagement",
            trigger: { type: "event", event: "engagement" },
          },
          {
            amount: 20000,
            label: "Earlier of working draft delivered or drop-dead date",
            trigger: { type: "earlier_of", event: "working_draft_delivered", date: null },
          },
        ],
      },
    },
    {
      key: "loan_documents",
      label: "Loan Documents Fee",
      type: "flat",
      amount: 15000,
      deliverableKeys: [
        "loan_loan_agreement",
        "loan_promissory_note",
        "loan_pledge_security",
        "loan_mortgage",
        "loan_ucc1",
      ],
      revisionsHourly: true,
      conditional: false,
      billing: {
        kind: "single",
        trigger: { type: "event", event: "loan_documents_delivered" },
      },
    },
    {
      key: "sec_form_d",
      label: "SEC Form D Filing Fee",
      type: "flat",
      amount: 1750,
      deliverableKeys: ["sec_form_d"],
      revisionsHourly: false,
      conditional: true,
      billing: {
        kind: "single",
        trigger: { type: "event", event: "offering_commenced" },
      },
    },
    {
      key: "intercreditor",
      label: "Intercreditor Agreement (hourly)",
      type: "hourly",
      amount: null,
      deliverableKeys: ["intercreditor_agreement"],
      revisionsHourly: false,
      conditional: true,
      billing: null,
    },
  ],
};
